import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { auth, signInWithCustomToken } from '@/lib/firebase';
import { apiPost } from '@/services/api';
import { logger } from '@/lib/logger';
import { ArrowLeft } from 'lucide-react';
import logoUrl from '@assets/logo_square_1753226114988.png';

const RESEND_COOLDOWN = 30;

export default function VerifyOtp() {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const email = sessionStorage.getItem('otp_email') || '';

  useEffect(() => {
    if (!email) {
      setLocation('/login');
      return;
    }
    inputRefs.current[0]?.focus();
  }, [email, setLocation]);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      await apiPost('/api/auth/otp/request', { email }, false);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      startCooldown();
      toast({ title: "Code resent", description: `A new code has been sent to ${email}.` });
    } catch (error: any) {
      logger.error('Resend error:', error);
      toast({ title: "Failed to resend", description: error.message || "Please try again.", variant: "destructive" });
    } finally {
      setResending(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleBack = () => {
    sessionStorage.removeItem('otp_email');
    setLocation('/login');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const passcode = otp.join('');
    if (passcode.length !== 6) {
      toast({ title: "Invalid code", description: "Please enter the complete 6-digit code.", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      logger.log('🔐 Verifying passcode for:', email);
      const data = await apiPost<{ token: string }>('/api/auth/otp/verify', { email, passcode }, false);

      if (!data.token) throw new Error('Code does not match');

      logger.log('✅ Passcode verified, signing in');
      setSigningIn(true);
      await signInWithCustomToken(auth, data.token);

      sessionStorage.removeItem('otp_email');

      const rawPath = sessionStorage.getItem('intendedPath');
      const intendedPath = rawPath && rawPath.startsWith('/') && rawPath !== '/login' ? rawPath : '/';
      sessionStorage.removeItem('intendedPath');

      logger.log('✅ Successfully signed in');
      setLocation(intendedPath);
    } catch (error: any) {
      logger.error('Verification error:', error);
      toast({
        title: "Verification failed",
        description: error.message || "Code does not match. Please try again.",
        variant: "destructive",
      });
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setLoading(false);
      setSigningIn(false);
    }
  };

  if (!email) return null;

  if (signingIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595ebe] mx-auto"></div>
          <p className="mt-4 text-slate-600">Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-[#1f2937] text-white py-16">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center justify-center gap-3">
            <img src={logoUrl} alt="Wellbound+" className="h-10 w-10" />
            <h1 className="text-2xl font-semibold">Wellbound+</h1>
          </div>
          <p className="text-white/80 text-base mt-2 text-center">Admin Tools</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 pt-20">
        <div className="max-w-md w-full">
          <Card className="shadow-md border-0">
            <CardContent className="p-8">
              <button
                onClick={handleBack}
                className="flex items-center text-sm text-slate-500 hover:text-slate-800 mb-6"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </button>

              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-slate-900">Enter verification code</h2>
                <p className="mt-1 text-sm text-slate-500">
                  We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex justify-center gap-2" onPaste={handlePaste}>
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="password"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-semibold bg-slate-50 border border-slate-200"
                      disabled={loading}
                    />
                  ))}
                </div>

                <Button
                  type="submit"
                  disabled={loading || otp.join('').length !== 6}
                  className="w-full bg-[#595ebe] hover:bg-[#595ebe]/90 text-white py-2.5 px-4 rounded font-medium transition-colors"
                >
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>

                <div className="text-center text-sm text-slate-500">
                  Didn't receive a code?{' '}
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={cooldown > 0 || resending || loading}
                    className="text-[#595ebe] hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline font-medium"
                  >
                    {resending ? 'Sending...' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
